import { FormEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Headset, Mail, SendHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { StatusBadge } from '../../../components/design-system/admin/StatusBadge';
import { onRealtime } from '../../../common/services/realtime.service';
import { HandoffSummary, TeamHandoff, useShopperInsightsApi } from '../../../common/hooks/use-shopper-insights-api.hook';

/** The socket event the API pushes when a shopper asks for the team, writes, or the team answers. */
export const HANDOFF_LIVE_EVENT = 'shopper:handoff';

export const handoffOpen = (handoff?: TeamHandoff | null) =>
    !!handoff && (handoff.status === 'requested' || handoff.status === 'active');

/**
 * How many shoppers are waiting for a person, kept current over the socket.
 * `refresh` also lets the page ask again after it changed something itself.
 */
export function useHandoffSummary() {
    const api = useShopperInsightsApi();
    const [summary, setSummary] = useState<HandoffSummary | null>(null);

    const refresh = useCallback(async () => {
        try {
            setSummary(await api.getHandoffSummary());
        } catch {
            // An older API, or a plan without the chat: the inbox works without the counts.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        void refresh();
        return onRealtime(HANDOFF_LIVE_EVENT, () => { void refresh(); });
    }, [refresh]);

    return { summary, refresh };
}

/** The row's word on the team: waiting for you, or who is answering. */
export function TeamBadge({ handoff }: { handoff?: TeamHandoff | null }) {
    const { t } = useTranslation();
    if (!handoff) return null;
    const [tone, label] = handoff.status === 'requested'
        ? ['warning' as const, t('SHOPPER_TEAM.BADGE_WAITING')]
        : handoff.status === 'active'
            ? ['info' as const, handoff.agentName ? t('SHOPPER_TEAM.BADGE_WITH', { name: handoff.agentName }) : t('SHOPPER_TEAM.BADGE_WITH_TEAM')]
            : ['neutral' as const, t('SHOPPER_TEAM.BADGE_CLOSED')];
    return (
        <span data-testid="team-badge" data-status={handoff.status}>
            <StatusBadge tone={tone} className={tone === 'neutral' ? 'text-muted-foreground' : undefined}>{label}</StatusBadge>
        </span>
    );
}

/**
 * Where the team stands in one conversation, and what to do about it: take
 * it, or hand it back to the assistant. The shopper's email shows only when
 * they typed it in for the reply.
 */
export function TeamStatus({
    handoff,
    onTake,
    onClose,
    busy,
}: {
    handoff: TeamHandoff;
    onTake: () => void;
    onClose: () => void;
    busy?: boolean;
}) {
    const { t } = useTranslation();
    const open = handoffOpen(handoff);
    const title = handoff.status === 'requested'
        ? t('SHOPPER_TEAM.STATUS_WAITING')
        : handoff.status === 'active'
            ? (handoff.agentName ? t('SHOPPER_TEAM.STATUS_ACTIVE', { name: handoff.agentName }) : t('SHOPPER_TEAM.STATUS_ACTIVE_TEAM'))
            : t('SHOPPER_TEAM.STATUS_CLOSED');

    return (
        <section
            className="flex flex-col gap-3 border-b px-6 py-4"
            data-testid="team-status"
            data-status={handoff.status}
            aria-label={title}
        >
            <div className="flex items-start gap-3">
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border text-muted-foreground">
                    <Headset className="size-4" aria-hidden />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <p className="text-sm font-medium">{title}</p>
                    <p className="text-xs text-muted-foreground">
                        {t(`SHOPPER_TEAM.REASON.${handoff.reason}`, { defaultValue: '' })}
                        {open ? ` · ${t('SHOPPER_TEAM.ASSISTANT_PAUSED')}` : ''}
                    </p>
                    {handoff.shopperEmail && (
                        <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Mail className="size-3.5" aria-hidden />
                            {t('SHOPPER_TEAM.SHOPPER_EMAIL', { email: handoff.shopperEmail })}
                        </p>
                    )}
                </div>
            </div>
            {open && (
                <div className="flex flex-wrap gap-2">
                    {handoff.status === 'requested' && (
                        <Button variant="outline" size="sm" onClick={onTake} disabled={busy} data-testid="team-take">
                            {t('SHOPPER_TEAM.TAKE')}
                        </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={busy} data-testid="team-close">
                        {t('SHOPPER_TEAM.CLOSE')}
                    </Button>
                </div>
            )}
        </section>
    );
}

/** The team's reply box, at the foot of the conversation. Enter sends; Shift+Enter is a new line. */
export function TeamComposer({
    onSend,
    hint,
}: {
    onSend: (text: string) => Promise<boolean>;
    /** A line above the box: what replying does, or that the shopper gets it by email. */
    hint?: string;
}) {
    const { t } = useTranslation();
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [failed, setFailed] = useState(false);

    const send = async (event?: FormEvent) => {
        event?.preventDefault();
        const body = text.trim();
        if (!body || sending) return;
        setSending(true);
        setFailed(false);
        const ok = await onSend(body);
        setSending(false);
        if (ok) setText(''); else setFailed(true);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            void send();
        }
    };

    return (
        <form onSubmit={send} className="sticky bottom-0 flex flex-col gap-2 border-t bg-background px-6 py-4" data-testid="team-composer">
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
            <div className="flex items-end gap-2">
                <Textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={t('SHOPPER_TEAM.REPLY_PLACEHOLDER') as string}
                    aria-label={t('SHOPPER_TEAM.REPLY_PLACEHOLDER') as string}
                    rows={2}
                    maxLength={2000}
                    className="min-h-[44px] resize-none text-sm"
                />
                <Button type="submit" variant="outline" disabled={!text.trim() || sending} data-testid="team-send">
                    <SendHorizontal className="rtl:rotate-180" />
                    {t('SHOPPER_TEAM.SEND')}
                </Button>
            </div>
            {failed && <p role="alert" className="text-xs text-destructive">{t('SHOPPER_TEAM.SEND_FAILED')}</p>}
        </form>
    );
}
